/* eslint-disable camelcase */
import { Article_article_banner, ReferenceType } from '@modules/client';
import AppBanner from '@modules/layout/layout/components/AppBanner';
import AppMainNavbar from '@modules/layout/layout/components/AppMainNavbar';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import AppContent from '@modules/layout/layout/components/AppContent';
import AppHeading from '@modules/layout/layout/components/AppHeading';
import AppLayout, { TOpenGraphMeta } from '@modules/layout/layout/components/AppLayout';
import AppLeftNav from '@modules/layout/layout/components/AppLeftNav';
import AppRightNav from '@modules/layout/layout/components/AppRightNav';
import AppRightNavContent from '@modules/ui/shared/components/AppRightNavContent';
import SideBarNavigation from '@modules/ui/shared/components/SideBarNavigation';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import useHeadingGroupStyles from '@modules/ui/shared/styles/HeadingGroup.styles';
import Heading from '@modules/utilities/interfaces/Heading';
import { MarkdownField } from '@modules/utilities/types';
import { useTranslation } from '@rbx/intl';
import { Grid } from '@rbx/ui';
import React, { FunctionComponent, useMemo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { ArticleSchemaMarkupContext } from '@modules/seo/structured-data/types';
import { TestId } from '@modules/utilities/constants/testConstants';
import CloudAuthenticationClient from '@modules/clients/cloudAuthenticationClient';
import throwError from '@modules/utilities/error';
import HeadingItem from '@modules/utilities/interfaces/HeadingItem';
import AppIAHeading from '@modules/layout/layout/components/AppIAHeading';
import ExploreCoursesBanner from '@modules/learning/components/ExploreCoursesBanner';
import CloudIABanner from '@modules/openCloud/CloudIABanner';
import EngineReferenceLandingTabs from '@modules/apiReference/landing/EngineReferenceLandingTabs';
import {
  isIAV1FeaturePage,
  isIAV1CloudApiPage,
  isCloudHomePage,
} from '@modules/ui/shared/utils/cloudPages';
import Article from './Article';
import useArticleContainerStyles from './ArticleContainer.styles';
import ArticleNavigationSection, {
  ArticleNavigationSectionProps,
} from './ArticleNavigationSection';
import ScopeHeadings from '../../openCloud/page/main/utils/scopeHeadings';

export interface ArticleContainerProps extends ArticleNavigationSectionProps, Heading {
  title: string;
  description: string | null;
  content: string | MarkdownField;
  banner?: Omit<Article_article_banner, '__typename'>;
  openGraphMeta: Partial<TOpenGraphMeta>;
  seoStructureData?: ArticleSchemaMarkupContext;
  slug?: string;
}

const ScopePageUrlPath = 'cloud/reference/scopes';

const ArticleContainer: FunctionComponent<React.PropsWithChildren<ArticleContainerProps>> = ({
  banner,
  slug,
  title,
  description,
  content,
  next,
  prev,
  headings,
  seoStructureData,
  openGraphMeta,
}) => {
  const router = useRouter();
  const {
    classes: { root, articleHeader },
    cx,
  } = useHeadingGroupStyles();
  const {
    classes: { container },
  } = useArticleContainerStyles();
  const { getBreadcrumbsItems } = useSideBarContent();
  const { translate } = useTranslation();
  const breadcrumbsItems = useMemo(() => {
    return getBreadcrumbsItems(translate);
  }, [getBreadcrumbsItems, translate]);
  const [dynamicHeadings, setDynamicHeadings] = useState<HeadingItem[]>(headings);
  const isScopesPage =
    typeof window !== 'undefined' && window.location.pathname.endsWith(ScopePageUrlPath);

  useEffect(() => {
    if (!isScopesPage) {
      setDynamicHeadings(headings);
      return;
    }
    const fetchScopesHeadings = async () => {
      try {
        const client = new CloudAuthenticationClient();
        const data = await client.getScopeTypes();
        const scopeHeadingsData = ScopeHeadings(data.scopeTypes || []);
        setDynamicHeadings(scopeHeadingsData);
      } catch (error) {
        throwError(`ScopeApiMap: Failed to fetch scope types: ${error}`);
        setDynamicHeadings(headings);
      }
    };
    fetchScopesHeadings();
  }, [isScopesPage, headings]);

  const { hideBreadcrumbs, enableNewLayout, enableEngineReferenceRevamp } = useAppConfig();
  const showBreadcrumbs =
    !banner && !hideBreadcrumbs && breadcrumbsItems && breadcrumbsItems.length > 1;

  // Check if this is a cloud features or apis markdown page
  const isCloudFeaturesOrApisPage = useMemo(() => {
    if (!process.env.enableOpenCloudIAV2Pages) {
      return false;
    }
    const path = router.asPath?.split('?')[0] ?? '';
    return isIAV1FeaturePage(path) || isIAV1CloudApiPage(path) || isCloudHomePage(path);
  }, [router.asPath]);

  const renderContentBasedOnPath = () => {
    const pathName = window.location.pathname;
    if (enableEngineReferenceRevamp) {
      if (pathName.endsWith('/reference/engine')) {
        return <EngineReferenceLandingTabs referenceType={ReferenceType.CLASS} showIntroduction />;
      }
      if (pathName.endsWith('/reference/engine/classes')) {
        return <EngineReferenceLandingTabs referenceType={ReferenceType.CLASS} />;
      }
      if (pathName.endsWith('/reference/engine/datatypes')) {
        return <EngineReferenceLandingTabs referenceType={ReferenceType.DATATYPE} />;
      }
      if (pathName.endsWith('/reference/engine/enums')) {
        return <EngineReferenceLandingTabs referenceType={ReferenceType.ENUM} />;
      }
      if (pathName.endsWith('/reference/engine/globals')) {
        return <EngineReferenceLandingTabs referenceType={ReferenceType.GLOBAL} />;
      }
      if (pathName.endsWith('/reference/engine/libraries')) {
        return <EngineReferenceLandingTabs referenceType={ReferenceType.LIBRARY} />;
      }
    }
    return <Article content={content} />;
  };

  return (
    <AppLayout
      pageTitle={title}
      testid={TestId.ArticleContainer}
      pageMetaDescription={description}
      headings={dynamicHeadings}
      openGraphMeta={openGraphMeta}
      seoStructureData={seoStructureData}
      slug={slug}
      renderBanner={(className?: string) => (
        <React.Fragment>
          {isCloudFeaturesOrApisPage && <CloudIABanner />}
          {enableNewLayout && <ExploreCoursesBanner enableNewLayout />}
          {banner && (
            <AppBanner
              className={className}
              backgroundUrl={banner.backgroundUrl}
              subtitle={banner.subtitle ?? undefined}
              title={banner.title}
            />
          )}
        </React.Fragment>
      )}>
      <AppIAHeading breadcrumbsItems={showBreadcrumbs ? breadcrumbsItems : undefined} />
      <AppMainNavbar
        pageTitle={title}
        breadcrumbsItems={showBreadcrumbs ? breadcrumbsItems : undefined}
      />
      {!banner && <AppHeading title={title} />}
      <AppRightNav content={<AppRightNavContent allowExpand={false} />} />
      <AppContent>
        <Grid
          component='article'
          container
          direction='column'
          wrap='nowrap'
          className={cx(root, container, articleHeader)}>
          {renderContentBasedOnPath()}
        </Grid>
        <ArticleNavigationSection next={next} prev={prev} />
      </AppContent>
      <AppLeftNav>
        <SideBarNavigation />
      </AppLeftNav>
    </AppLayout>
  );
};

export default ArticleContainer;
